"use client";

// WO-GF-001-P1B-S4 · Item 1 — SHARED POPUP SHELL. Extracted verbatim from TransportSearchLayer (B4.3) so the
// whole S3 popup framework (sponsor modal, W3 selectors, …) reuses ONE dialog shell instead of each layer
// re-inventing a modal — the "one shared popup layer serves them all" rationale (INTERACTION-INVENTORY §5
// row 4). Byte-for-byte the same render TransportSearchLayer already ships, so the transport modals are
// unchanged (behavior + pixel re-verified). Backdrop-click + Escape close; `...rest` passes through the
// `data-modal="…"` hook the behavior harness asserts on.

import { useEffect } from "react";

// Card 86bb671pf — A MODAL LAYER CONSUMES ESCAPE. Found by driving the funnel, not by reading it: with
// the membership-info modal open over the sign-up popup, ONE Escape closed both — the modal and the whole
// auth dialog under it, unmounting SignupFunnel and discarding everything the visitor had typed.
//
// Two fixes were tried and measured before this one, and both are worth recording because each looked
// right:
//   1. A per-instance `if (showMembership) return` in LoginPopup. Did nothing — the root app mounts
//      <LoginPopup/> MORE THAN ONCE per page (`[[...slug]]/page.tsx` 380 + 705, HubChromeLayer on the hub
//      pages). Each mount carries its own Escape listener over the SAME store flag, so the mount that
//      opened the modal stood down and its sibling, sitting at the guest menu, closed the dialog anyway.
//   2. A module-level "is any layer open?" counter, consulted by those handlers. Also did nothing, and the
//      reason is the useful part: React flushes a discrete-event update synchronously, so by the time the
//      window-level listener ran, this shell's own document listener had already closed the modal, the
//      unmount cleanup had decremented the counter, and the guard read 0. Measured: `__modalLayers()` was
//      1 with the modal open and 0 immediately after the keypress, with `setAuthOpen(false)` still firing.
//
// A flag read at handler time cannot express "someone above me already dealt with this key". Ownership of
// the event can: the shell listens on WINDOW in the CAPTURE phase, which is the first stop for any keydown
// in the document, and stops propagation there. Nothing downstream — document listeners in either phase,
// window bubble — sees the key at all, so no handler under the layer can act on stale state, whatever
// order React flushes in. Layers below are unaffected when no modal is open, because then nothing is
// registered here.
export function ModalShell({
  title,
  onClose,
  children,
  ...rest
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
} & Record<string, unknown>) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      {...rest}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, width: "min(880px, 92vw)", padding: 20, boxShadow: "0 12px 48px rgba(0,0,0,0.3)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h4>
          <button type="button" aria-label="Close" onClick={onClose} style={{ border: "none", background: "none", fontSize: 22, cursor: "pointer" }}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function btn(kind: "primary" | "outline"): React.CSSProperties {
  return kind === "primary"
    ? { flex: 1, padding: "10px 14px", borderRadius: 8, border: "none", background: "#111", color: "#fff", cursor: "pointer", fontWeight: 600 }
    : { flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid #bbb", background: "#fff", color: "#333", cursor: "pointer" };
}
